// Spawn-legality extension driven by EXTEND_SPAWN_LEGAL_AT_COVERED
// entries on in-play auras. Crusade / Jihad / Eclipse / Summoning
// Sphere / Evil Presence pattern: the aura's covered SITE cells become
// legal summon destinations for cards matching the entry's filter,
// regardless of whether those cells satisfy the standard "controlled
// site" rule.
//
// S7. Every card in the family reads "affected SITES" ("You may
// summon fire minions to affected sites"), so a covered cell only
// extends spawn legality while a site occupies it. Any controller's,
// and Rubble counts (codex: "Rubble is treated the same as any other
// site"). A covered VOID never becomes summonable through these auras
// (Voidwalk keeps its own base-rule void path).
//
// W4: server-consumed via sync. The server derives per-source
// `boardLegality.spawnExtensions` records (collectSpawnExtensions)
// and gates minion casts on them; auth clients read that bag from
// the mirror (spawnExtensionCellsFromLegality) instead of running
// auraExtendedSpawnCells locally. The mesh-walking half stays for
// sandbox / solo mode only.
//
// Pure. No GameBoard state. Returns a Set<'col,row'> that callers
// union into `spawnableCellsForMinion`'s `extraCells` parameter.
//
// The aura's controller becomes the filter context's "self" for
// controller-relative tests. Crusade's "earth minions to affected
// sites" implicitly means YOUR earth minions (the aura's controller).

import { matchesFilter } from '../cardFilter';
import { anchorCoveredCells } from '../../core/auraPlacement';
import { OP, TRIGGER } from '../schema';

// S7. Cells currently holding a Site of any kind (any controller,
// Rubble included). Extension footprints are intersected with this set.
function siteCellKeys(meshes, cellAt) {
  const out = new Set();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (cell) out.add(`${cell.col},${cell.row}`);
  }
  return out;
}

/**
 * Walk every in-play aura looking for EXTEND_SPAWN_LEGAL_AT_COVERED
 * entries whose filter matches the candidate card. Union their
 * covered cells into the result.
 *
 * @param {object} card             - hand instance the player wants to summon
 * @param {object|null} fullCard    - catalog row for `card`
 * @param {Iterable<[any, { userData, position }]>} meshes
 * @param {(x: number, z: number) => { col, row } | null} cellAt
 * @param {(cardId: string) => object | null} getFullCard
 * @param {'p1'|'p2'} controller    - the player attempting the summon
 * @returns {Set<string>}
 */
export function auraExtendedSpawnCells(card, fullCard, meshes, cellAt, getFullCard, controller) {
  const out = new Set();
  if (!card) return out;
  const siteCells = siteCellKeys(meshes, cellAt);

  // Merge live + catalog so filter checks on element/subtype/type
  // work whether the hand instance carries those fields or not.
  // `rotated` stays from the live card so allied/enemy checks reflect
  // the current controller.
  const cardForFilter = {
    ...fullCard,
    ...card,
    type: card.type || fullCard?.type,
    subTypes: card.subTypes || fullCard?.subTypes,
    elements: card.elements || fullCard?.elements,
    rotated: controller === 'p2',
  };

  for (const [, mesh] of meshes) {
    const aura = mesh?.userData?.cardInstance;
    if (!aura || aura.type !== 'Aura') continue;
    if (!aura._auraShape || !aura._auraAnchor) continue;
    const fullAura = getFullCard(aura.cardId);
    if (!fullAura) continue;
    const entries = (fullAura.automation || []).filter((e) => e.trigger === TRIGGER.STATIC);
    let auraGrants = false;
    for (const entry of entries) {
      for (const eff of (entry.effect || [])) {
        if (eff.op !== OP.EXTEND_SPAWN_LEGAL_AT_COVERED) continue;
        const filter = eff.args?.filter || {};
        const ctx = { source: aura, effectiveRotated: !!aura.rotated };
        if (matchesFilter(cardForFilter, filter, ctx)) {
          auraGrants = true;
          break;
        }
      }
      if (auraGrants) break;
    }
    if (!auraGrants) continue;
    for (const key of anchorCoveredCells(aura._auraAnchor, aura._auraShape.shape)) {
      if (siteCells.has(key)) out.add(key); // S7: "affected sites" only
    }
  }
  return out;
}

/**
 * W4. Per-source spawn-extension records for the server-derived
 * `boardLegality.spawnExtensions` bag. One record per (aura, entry)
 * carrying everything consumption needs to match a candidate later:
 * `{ sourceId, controller, filter, cells }`. `cells` is the covered
 * footprint intersected with the cells that HOLD a site (S7.
 * "affected sites"); the bag re-derives after every commit, so a site
 * entering or leaving a covered cell updates the record. Sorted
 * (sourceId, then cells) so a stable board derives byte-identical
 * output.
 *
 * @param {Iterable<[any, { userData, position }]>} meshes
 * @param {(x: number, z: number) => { col, row } | null} cellAt
 * @param {(cardId: string) => object | null} getFullCard
 * @returns {Array<{ sourceId, controller, filter, cells: string[] }>}
 */
export function collectSpawnExtensions(meshes, cellAt, getFullCard) {
  const out = [];
  const siteCells = siteCellKeys(meshes, cellAt);
  for (const [, mesh] of meshes) {
    const aura = mesh?.userData?.cardInstance;
    if (!aura || aura.type !== 'Aura') continue;
    if (!aura._auraShape || !aura._auraAnchor) continue;
    const fullAura = getFullCard(aura.cardId);
    if (!fullAura) continue;
    for (const entry of (fullAura.automation || [])) {
      if (entry.trigger !== TRIGGER.STATIC) continue;
      for (const eff of (entry.effect || [])) {
        if (eff.op !== OP.EXTEND_SPAWN_LEGAL_AT_COVERED) continue;
        out.push({
          sourceId: aura.id,
          controller: aura.rotated ? 'p2' : 'p1',
          filter: eff.args?.filter || {},
          cells: [...anchorCoveredCells(aura._auraAnchor, aura._auraShape.shape)]
            .filter((key) => siteCells.has(key))
            .sort(),
        });
      }
    }
  }
  return out.sort((a, b) => (a.sourceId < b.sourceId ? -1 : a.sourceId > b.sourceId ? 1 : 0));
}

/**
 * W4. Candidate matching against derived spawn-extension records:
 * the exact filter semantics of `auraExtendedSpawnCells`, run off the
 * projected `boardLegality.spawnExtensions` bag instead of live
 * meshes. Both the server's minion-cast gate and the auth client's
 * placement halo consume THIS function, so they can never disagree.
 *
 * @param {Array|null} extensions - boardLegality.spawnExtensions
 * @param {object} card           - the summon candidate (hand instance)
 * @param {object|null} fullCard  - catalog row for `card`
 * @param {'p1'|'p2'} controller  - the player attempting the summon
 * @returns {Set<string>}
 */
export function spawnExtensionCellsFromLegality(extensions, card, fullCard, controller) {
  const out = new Set();
  if (!card || !Array.isArray(extensions)) return out;
  const cardForFilter = {
    ...fullCard,
    ...card,
    type: card.type || fullCard?.type,
    subTypes: card.subTypes || fullCard?.subTypes,
    elements: card.elements || fullCard?.elements,
    rotated: controller === 'p2',
  };
  for (const ext of extensions) {
    const rotated = ext.controller === 'p2';
    const ctx = { source: { id: ext.sourceId, rotated }, effectiveRotated: rotated };
    if (!matchesFilter(cardForFilter, ext.filter || {}, ctx)) continue;
    for (const key of ext.cells || []) out.add(key);
  }
  return out;
}
